import React, { useMemo, useState } from "react";
import { Alert, Box, CircularProgress, IconButton, Menu, MenuItem, Select, SelectChangeEvent, Slider, Stack, Typography, useTheme } from "@mui/material";
import { ResponsiveContainer, ComposedChart, CartesianGrid, XAxis, YAxis, Tooltip, Area, Line } from "recharts";
import { useForecast } from "../../hooks/useForecast";
import { WeeklyForecastComponentType } from "@domain/forecast/enums/WeeklyForecastComponentType.enum";
import { APP_CONFIG } from "../../../../config/app.config";
import { MoreVert } from "@mui/icons-material";
import { useProduct } from "@features/products";
import { TrendMode } from "@domain/forecast/models/ForecastParameters";
import { InfluenceLabel } from "../ForecastAnalytics/InfluenceLabel";
import { ForecastSettingsMenu } from "../ForecastSettingsMenu";
import { ComponentGraph } from "../Graph/ComponentGraph";
import { ForecastSourceType } from "@domain/forecast/models/Forecast";
import { useProductUpdate } from "@features/products/hooks/useProductUpdate";

interface TimeTravelTrendProps {
  sourceType: ForecastSourceType; // e.g. 'search'
  source: string;     // e.g. search term value
  connector: string;  // e.g. connector type
  canModifyParameters?: boolean;
}

// Simple week -> date helper for display only (approximate ISO week to date)
function weekYearToDateString(year: number, week: number): string {
  const date = new Date(year, 0, 1);
  const day = date.getDay();
  const diff = (week - 1) * 7 + (day <= 4 ? 1 - day : 8 - day);
  date.setDate(date.getDate() + diff);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

const TimeTravelTrend: React.FC<TimeTravelTrendProps> = ({
  sourceType,
  source,
  connector,
  canModifyParameters = false,
}) => {
  const theme = useTheme();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { forecast, isLoading, error } = useForecast({ sourceType, source, connector });

  const { product } = useProduct(source);
  const { updateProduct } = useProductUpdate();

  const chartData = useMemo(() => {
    if (!forecast?.weeklyForecastComponents) return [];

    // Filter by the specified component type
    const components = forecast.weeklyForecastComponents
      .filter(comp => comp.type === WeeklyForecastComponentType.TREND);

    if (components.length === 0) return [];

    // Sort chronologically
    const sorted = components
      .slice()
      .sort((a, b) => (a.year === b.year ? a.week - b.week : a.year - b.year));

    // Calculate moving average
    const windowSize = 10; // e.g. 3-week moving average
    const movAvg = (arr: number[], idx: number, size: number) => {
      const start = Math.max(0, idx - Math.floor((size - 1) / 2));
      const end = Math.min(arr.length, start + size);
      const actualSize = end - start;
      const sum = arr.slice(start, end).reduce((acc, val) => acc + val, 0);
      return actualSize > 0 ? sum / actualSize : 0;
    };

    // Extract forecast quantities
    const forecastQuantities = sorted.map(w => w.forecastQuantity ?? 0);

    return sorted.map((w, i) => ({
      date: weekYearToDateString(w.year, w.week),
      year: w.year,
      week: w.week,
      forecast: movAvg(forecastQuantities, i, windowSize),
      forecastMin: movAvg(forecastQuantities, i, windowSize) - 1,
      forecastMax: movAvg(forecastQuantities, i, windowSize) + 1,
      forecastBands: [movAvg(forecastQuantities, i, windowSize) - 1, movAvg(forecastQuantities, i, windowSize) + 1],
    }));
  }, [forecast]);


  const getSliderDefaultValue = () => {
    if (forecast?.parameters.changepoint_prior_scale) {
      return forecast.parameters.changepoint_prior_scale * 10;
    }

    return 5;
  };

  const handleSliderChange = (influence: number) => {
    updateProduct({
      productId: source,
      updateData: {
        forecastParameters: {
          ...product?.forecastParameters ?? {},
          changepoint_prior_scale: influence / 10
        }
      }
    });
  };


  const handleTrendModeChange = (mode: TrendMode) => {
    updateProduct({
      productId: source,
      updateData: {
        forecastParameters: {
          ...product?.forecastParameters ?? {},
          growth: mode as TrendMode
        }
      }
    });
  };

  return (
    <Box sx={{
      position: 'relative',
      border: `1px solid ${APP_CONFIG.ui.theme.colors.border}`,
      borderRadius: 2,
      padding: 2,
      flex: 1,
    }}>
      <Stack direction="row" spacing={0.5} flex={1} justifyContent="space-between" alignItems="center">
        <Typography variant="subtitle2" color="text.secondary">
          Trend
        </Typography>

        {canModifyParameters && (
          <IconButton size="small"
            onClick={(event) => setAnchorEl(event.currentTarget)}
          ><MoreVert /></IconButton>
        )}


        <ForecastSettingsMenu
          title="Trend Settings"
          onTrendModeChange={handleTrendModeChange} onInfluenceChange={handleSliderChange} defaultTrendMode={forecast?.parameters.growth} defaultInfluence={getSliderDefaultValue()} anchorEl={anchorEl} onClose={() => setAnchorEl(null)} />
      </Stack>

      <Typography variant="h6" color="text.secondary">{forecast?.parameters.growth}</Typography>


      <InfluenceLabel influence={forecast?.forecastAnalytics?.trend_influence} />

      {isLoading && (
        <Box display="flex" justifyContent="center" alignItems="center" height={150}>
          <CircularProgress />
        </Box>
      )}

      <ComponentGraph chartData={chartData} />
    </Box>
  );
};

export default TimeTravelTrend;

